import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { setCloseoutProfile } from "../../lib/closeout";
import { useAsync } from "../../lib/hooks";
import { fetchCompanySettings } from "../../lib/queries";
import { useSession } from "../../session";

/**
 * Who finished jobs are sent to close — Kelsey (ADR 0273).
 *
 * A person rather than an address: the same choice puts their tab across the
 * top, opens the close-out board on it, and sends the summary to their own
 * email. Two settings could point at two people; one cannot.
 *
 * "Nobody" hides the tab and leaves Send to Kelsey off every job, which is
 * said on the checklist rather than failing quietly.
 *
 * Anyone on staff can change it (Jonathon, 2026-10-12: "anyone can close
 * them"). The company row is written by `admin`, which every staff login
 * holds, so the picker follows that role, as the Thursday reminder's does.
 */
export function CloseoutPerson() {
  const { isAdmin, people } = useSession();
  const mayChoose = isAdmin;
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const settings = useAsync(fetchCompanySettings, []);

  if (settings.loading) return <div className="stub">{t("Checking…")}</div>;

  const chosenId = settings.data?.closeout_profile_id ?? null;
  const chosen = people.find((p) => p.id === chosenId) ?? null;
  // Somebody still here, with an address to send to.
  const options = people.filter(
    (person) => person.active !== false && Boolean(person.email),
  );

  async function choose(value: string) {
    setSaving(true);
    setError(null);
    try {
      await setCloseoutProfile(value || null);
      await settings.reload();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : String(problem));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="rrow" style={{ marginTop: 24 }}>
        <span className="k">{t("Finished jobs go to")}</span>
        <span className="v">
          <span className={`badge ${chosen ? "approved" : "draft"}`}>
            {chosen ? chosen.full_name : t("nobody")}
          </span>
        </span>
      </div>

      <ErrorNote error={error} />

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 520 }}>
        {t(
          "After Final Check a rep sends the job to this person to square with QuickBooks and close. They get a tab of every sold job and an email for each one sent.",
        )}
      </p>

      {mayChoose ? (
        <select
          className="addrow"
          aria-label={t("Finished jobs go to")}
          disabled={saving}
          value={chosenId ?? ""}
          onChange={(event) => void choose(event.target.value)}
        >
          <option value="">{t("Nobody")}</option>
          {options.map((person) => (
            <option key={person.id} value={person.id}>
              {person.full_name}
            </option>
          ))}
        </select>
      ) : (
        <div className="na-hint" style={{ maxWidth: 520 }}>
          {t("Ask an admin to change this.")}
        </div>
      )}
    </>
  );
}
